import React, { Component } from 'react';
import { connect } from "react-redux";
import { Form, Input, Row, Col, Button, Space, Tabs } from 'antd';
import LABELS from 'constants/labels';
import actions from 'store/actions';
import * as EmailValidator from 'email-validator';
import { RolePermissionList, RoleUserList, RoleApiKeyList, ProhibitedArea, FullHeightContainerLayout } from "components";
import { showError, showSuccess, showInfo } from 'common/ToastNotifications';
import { nameRules, descriptionRules } from 'common/FormValidationRules';
import moment from 'moment';
import _ from 'lodash';
import { diffRoleItems } from "common/roleDiff";
import { standardActionFlags } from "common/permissionActions";
import flatten from 'flat';
import { keyGenerator } from 'common/Utility';
import CypressTestIds from "../../../cypress/CypressTestIds";

const { TabPane } = Tabs;

class ManageRole extends Component {

    constructor(props) {
        super(props);
        this.state = {
            readOnlyView: this.props.action === "view",
            formErrors: {}
        };
        this.defaultRole = {
            name: '',
            description: '',
            permissions: [],
            users: []
        };
        this.emptyObject = {};
        this.formRef = React.createRef();
    }

    componentDidMount() {
        switch (this.props.action) {
            case "view":
            case "edit":
                this.populateRole();
                break;
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.formErrors && this.props.formErrors !== prevProps.formErrors) {
            this.setState({
                formErrors: this.props.formErrors
            });
        }
        if (this.props.role && prevProps.role !== this.props.role) {
            this.populateRole();
        }
    }

    populateRole = () => {
        if (this.props.role && this.formRef.current) {
            switch (this.props.action) {
                case "view":
                case "edit":
                    this.formRef.current.resetFields();
                    this.formRef.current.setFieldsValue(this.props.role)
                    break;
            }
        }
    }

    onFormSubmit = (role) => {
        if (!this.isSaveAllowed()) {
            return;
        }

        if (!this.validateRole(role)) {
            return;
        }

        if (this.props.action === "edit") {
            let updatedRoleData = {
                role: null,
                newUsers: [],
                deletedUsers: [],
                updatedUsers: [],
                newApiKeys: [],
                deletedApiKeys: [],
                updatedApiKeys: [],
                newPermissions: [],
                deletedPermissions: [],
                updatedPermissions: []
            };
            let originalRole = { ...this.props.role };
            let updatedRole = { ...role };

            let updatedUserData = this.getModifiedRoleUsers(originalRole.users, updatedRole.users);
            let updatedApiKeyData = this.getModifiedRoleApiKeys(originalRole.apiKeys, updatedRole.apiKeys);
            let updatedPermissionData = this.getModifiedRolePermissions(originalRole.permissions, updatedRole.permissions);

            updatedRoleData = { ...updatedRoleData, ...updatedUserData, ...updatedApiKeyData, ...updatedPermissionData };

            delete originalRole.users;
            delete originalRole.apiKeys;
            delete updatedRole.users;
            delete updatedRole.apiKeys;
            delete originalRole.permissions;
            delete updatedRole.permissions;

            updatedRole = { ...originalRole, ...updatedRole };
            if (_.isEqual(originalRole, updatedRole) === false) {
                updatedRoleData.role = updatedRole;
            }

            if (updatedRoleData.role ||
                updatedRoleData.newUsers.length > 0 ||
                updatedRoleData.deletedUsers.length > 0 ||
                updatedRoleData.updatedUsers.length > 0 ||
                updatedRoleData.newApiKeys.length > 0 ||
                updatedRoleData.deletedApiKeys.length > 0 ||
                updatedRoleData.updatedApiKeys.length > 0 ||
                updatedRoleData.newPermissions.length > 0 ||
                updatedRoleData.deletedPermissions.length > 0 ||
                updatedRoleData.updatedPermissions.length > 0) {
                this.props.updateRole(this.props.role.roleId, updatedRoleData);
            }
        }
        else {
            let users = role.users;
            let apiKeys = role.apiKeys;
            let permissions = role.permissions;

            delete role.users;
            delete role.apiKeys;
            delete role.permissions;

            this.props.createRole({ ...role, active: true }, permissions, users, apiKeys);
        }
    }

    getModifiedRoleUsers = (originalUsers, modifiedUsers) => {
        let { added, deleted, updated } = diffRoleItems(originalUsers, modifiedUsers, "userId");
        return { newUsers: added, deletedUsers: deleted, updatedUsers: updated };
    }

    getModifiedRoleApiKeys = (originalApiKeys, modifiedApiKeys) => {
        let { added, deleted, updated } = diffRoleItems(originalApiKeys, modifiedApiKeys, "apiKeyId");
        return { newApiKeys: added, deletedApiKeys: deleted, updatedApiKeys: updated };
    }

    getModifiedRolePermissions = (originalPermissions, modifiedPermissions) => {
        let { added, deleted, updated } = diffRoleItems(originalPermissions, modifiedPermissions, "permissionId");
        return { newPermissions: added, deletedPermissions: deleted, updatedPermissions: updated };
    }

    validateRole = (role) => {
        // Errors are rebuilt from the submitted rows rather than carried over from state, so a row that
        // was fixed or removed does not keep an error keyed to an index it no longer has.
        let formErrors = {};
        const check = (rows, listName, idField, itemLabel, selectMessage) => {
            if (!rows || rows.length === 0) {
                return;
            }
            let seen = new Set();
            rows.forEach((row, index) => {
                let id = row && row[idField];
                if (!id) {
                    formErrors[`${listName}.${index}.${idField}`] = selectMessage;
                }
                else if (seen.has(id)) {
                    // The same item picked twice: the later row is the one to fix. A row that was removed
                    // is no longer submitted, so removing and re-adding the same item is fine.
                    formErrors[`${listName}.${index}.${idField}`] = `${itemLabel} already added`;
                }
                else {
                    seen.add(id);
                }
            });
        };
        check(role.users, "users", "userId", "User", "Please select a user");
        check(role.permissions, "permissions", "name", "Permission", "Please select a permission");
        check(role.apiKeys, "apiKeys", "apiKeyId", "API key", "Please select an API key");

        this.setState({ formErrors });
        return Object.keys(formErrors).length === 0;
    }

    onPermissionChange = (value, field) => {
        let formErrors = { ...this.state.formErrors };
        delete formErrors[`permissions.${field.name}.name`];
        let permissions = this.formRef.current.getFieldValue("permissions");
        for (let index = 0; index < permissions.length; index++) {
            if (index !== field.name && permissions[index].name === value) {
                formErrors[`permissions.${field.name}.name`] = "Permission already added";
                break;
            }
        }
        let selectedPermission = this.props.permissionMasterData.find(item => item.name === value);
        if (selectedPermission) {
            permissions[field.name].name = selectedPermission.name;
            permissions[field.name].resourceName = selectedPermission.resourceName;
            permissions[field.name].resourceId = selectedPermission.resourceId;
            permissions[field.name].effect = selectedPermission.effect;
            permissions[field.name].condition = selectedPermission.condition;
            Object.assign(permissions[field.name], standardActionFlags(selectedPermission.actions));
            // A permission's own actions start unchecked: each is granted deliberately.
            permissions[field.name].otherActions = [];
            permissions[field.name].heldOtherActions = [];
            // The row now stands for another permission: saving removes the one it was loaded
            // with and adds this one, rather than writing these actions onto the old one.
            permissions[field.name].permissionId = null;
            this.formRef.current.setFieldsValue({
                permissions
            });
        }
        this.setState({
            formErrors
        });
    }

    onUserChange = (value, field) => {
        let formErrors = { ...this.state.formErrors };
        delete formErrors[`users.${field.name}.userId`];
        let users = this.formRef.current.getFieldValue("users");
        for (let index = 0; index < users.length; index++) {
            if (index !== field.name && users[index].userId === value) {
                formErrors[`users.${field.name}.userId`] = "User already added";
                break;
            }
        }
        let selectedUser = this.props.userList.find(item => item.userId === value);
        if (selectedUser) {
            users[field.name].userId = selectedUser.userId;
            users[field.name].email = selectedUser.email;
            users[field.name].firstName = selectedUser.firstName;
            users[field.name].lastName = selectedUser.lastName;
            users[field.name].version = selectedUser.version;
            this.formRef.current.setFieldsValue({
                users
            });
        }
        this.setState({
            formErrors
        });
    }

    // Rows carry the fields the roles service expects, populated from the selected api key.
    onApiKeyChange = (value, field) => {
        let formErrors = { ...this.state.formErrors };
        delete formErrors[`apiKeys.${field.name}.apiKeyId`];
        let apiKeys = this.formRef.current.getFieldValue("apiKeys");
        for (let index = 0; index < apiKeys.length; index++) {
            if (index !== field.name && apiKeys[index].apiKeyId === value) {
                formErrors[`apiKeys.${field.name}.apiKeyId`] = "API key already added";
                break;
            }
        }
        let selectedApiKey = this.props.apiKeyList.find(item => item.apiKeyId === value);
        if (selectedApiKey) {
            apiKeys[field.name].apiKeyId = selectedApiKey.apiKeyId;
            apiKeys[field.name].name = selectedApiKey.name;
            apiKeys[field.name].active = selectedApiKey.active;
            apiKeys[field.name].version = selectedApiKey.version;
            this.formRef.current.setFieldsValue({
                apiKeys
            });
        }
        this.setState({
            formErrors
        });
    }

    onDeletePermission = (field, callback = null) => {
        let formErrors = { ...this.state.formErrors };
        delete formErrors[`permissions.${field.name}.name`];
        this.setState({
            formErrors
        });
        if (callback) {
            callback();
        }
    }

    onDeleteUser = (field, callback = null) => {
        let formErrors = { ...this.state.formErrors };
        delete formErrors[`users.${field.name}.userId`];
        this.setState({
            formErrors
        });
        if (callback) {
            callback();
        }
    }

    onDeleteApiKey = (field, callback = null) => {
        let formErrors = { ...this.state.formErrors };
        delete formErrors[`apiKeys.${field.name}.apiKeyId`];
        this.setState({
            formErrors
        });
        if (callback) {
            callback();
        }
    }

    validatePermissionsAndUsers = () => {
        for (let permission of this.state.permissions) {
            if (!permission.name || Object.keys(permission.errors).length > 0) {
                return false;
            }
        }
        for (let user of this.state.users) {
            if (!user.userId) {
                return false;
            }
        }
        return true;
    }

    isReadOnlyView = () => {
        return (this.state.readOnlyView || !(this.props.permission.canEdit || this.props.permission.canAdd));
    }

    isSaveAllowed = () => {
        if (this.props.action === "edit") {
            return this.props.permission.canEdit;
        }
        else if (this.props.action === "create") {
            return this.props.permission.canAdd;
        }
        return false;
    }

    getFormErrors = fieldName => {
        let formErrors = { ...this.props.formErrors, ...this.state.formErrors };
        if (formErrors[fieldName]) {
            return {
                help: formErrors[fieldName],
                validateStatus: "error"
            }
        }
        return this.emptyObject;
    }

    render() {
        if (!this.props.permission || !(this.props.permission.canEdit || this.props.permission.canAdd || this.props.permission.canView)) {
            return <ProhibitedArea></ProhibitedArea>
        }
        return (
            <Form
                ref={this.formRef}
                name="basic"
                initialValues={this.defaultRole}
                onFinish={this.onFormSubmit}
                onValuesChange={this.props.onValuesChanged}
                style={{ height: "100%" }}>
                <Row style={{ flexDirection: "row", flexGrow: 1, height: "100%" }} wrap={false}>
                    <Col span={24} style={{ display: "flex", flexDirection: "column", height: "100%", paddingRight: "5px" }}>
                        <FullHeightContainerLayout
                            showHeader={true}
                            showFooter={true}
                            header={<Row>
                                <Col span={8} style={{ textAlign: 'left' }}>
                                    <Form.Item
                                        {...this.getFormErrors("name")}
                                        label="Role Name"
                                        name={["name"]}
                                        validateFirst={true}
                                        rules={nameRules}>
                                        <Input readOnly={this.isReadOnlyView()} data-testid={CypressTestIds.ROLES_MANAGE_ROLE_NAME_INPUT}/>
                                    </Form.Item>
                                </Col>
                                <Col span={16} style={{ textAlign: 'left', paddingLeft: "5px" }}>
                                    <Form.Item
                                        {...this.getFormErrors("description")}
                                        label="Role Description"
                                        name={["description"]}
                                        rules={descriptionRules}>
                                        <Input readOnly={this.isReadOnlyView()} data-testid={CypressTestIds.ROLES_MANAGE_ROLE_DESCRIPTION_INPUT}/>
                                    </Form.Item>
                                </Col>
                            </Row>}

                            contentStyle={{ paddingBottom: "5px" }}
                            content={
                                <Tabs defaultActiveKey="1" style={{ height: "100%" }}>
                                    <TabPane tab="Permissions" key="1" style={{ height: "100%" }} data-testid={CypressTestIds.ROLES_MANAGE_ROLE_TAB_PERMISSIONS_TAB}>
                                        <Form.List name={["permissions"]}>
                                            {(fields, { add, remove }) => {
                                                return <RolePermissionList
                                                    readOnly={this.isReadOnlyView()}
                                                    permissions={fields}
                                                    permissionMasterData={this.props.permissionMasterData}
                                                    formRef={this.formRef}
                                                    add={add}
                                                    remove={remove}
                                                    onPermissionChange={this.onPermissionChange}
                                                    onDeletePermission={this.onDeletePermission}
                                                    getFormErrors={this.getFormErrors} />;
                                            }}
                                        </Form.List>
                                    </TabPane>
                                    <TabPane tab="Users" key="2" style={{ height: "100%" }} data-testid={CypressTestIds.ROLES_MANAGE_ROLE_TAB_USERS_TAB}>
                                        <Form.List name={["users"]}>
                                            {(fields, { add, remove }) => {
                                                return <RoleUserList
                                                    readOnly={this.isReadOnlyView()}
                                                    userList={this.props.userList}
                                                    users={fields}
                                                    formRef={this.formRef}
                                                    add={add}
                                                    remove={remove}
                                                    onUserChange={this.onUserChange}
                                                    onDeleteUser={this.onDeleteUser}
                                                    getFormErrors={this.getFormErrors} />;
                                            }}
                                        </Form.List>
                                    </TabPane>
                                    <TabPane tab="API Keys" key="3" style={{ height: "100%" }} data-testid={CypressTestIds.ROLES_MANAGE_ROLE_TAB_CREDENTIALS_TAB}>
                                        <Form.List name={["apiKeys"]}>
                                            {(fields, { add, remove }) => {
                                                return <RoleApiKeyList
                                                    readOnly={this.isReadOnlyView()}
                                                    apiKeyList={this.props.apiKeyList}
                                                    apiKeys={fields}
                                                    formRef={this.formRef}
                                                    add={add}
                                                    remove={remove}
                                                    onApiKeyChange={this.onApiKeyChange}
                                                    onDeleteApiKey={this.onDeleteApiKey}
                                                    getFormErrors={this.getFormErrors} />;
                                            }}
                                        </Form.List>
                                    </TabPane>
                                </Tabs>}
                            footer={
                                <Row>
                                    <Col span={24} className="footer-right-column">
                                        <Space>
                                            <Button onClick={this.props.onCancel} data-testid={CypressTestIds.ROLES_MANAGE_ROLE_CANCEL_BUTTON}>Cancel</Button>
                                            <Button type="primary" htmlType="submit" disabled={this.isReadOnlyView() || !this.isSaveAllowed()} data-testid={CypressTestIds.ROLES_MANAGE_ROLE_SAVE_BUTTON}>Save</Button>
                                        </Space>
                                    </Col>
                                </Row>
                            }>
                        </FullHeightContainerLayout>
                    </Col>
                </Row>
            </Form>
        );
    }
}

export default ManageRole